import React, { useState, useEffect } from 'react';
import './TableInventoryFBA.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import api from '../api/client';

const TableInventoryFBA = () => {
  const [inventoryData, setInventoryData] = useState([]);

  // Generate daily columns from current date to end of year
  const currentDate = new Date();
  const yearEnd = new Date(currentDate.getFullYear(), 11, 31);
  const dailyColumns = [];
  let current = new Date(currentDate);
  while (current <= yearEnd) {
    dailyColumns.push({
      date: current.toISOString().split('T')[0],
      day: current.toLocaleDateString('en-US', { weekday: 'short' }),
      formattedDate: current.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }),
      month: current.toLocaleString('en-US', { month: 'long' })
    });
    current.setDate(current.getDate() + 1);
  }

  // Get next month's name for demand calculation
  const getNextMonth = (currentMonth) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    const currentIndex = months.indexOf(currentMonth);
    return months[(currentIndex + 1) % 12];
  };

  useEffect(() => {
    api.get('/api/table-inventory-fba')
      .then((response) => response.data)
      .then((data) => {
        const processedData = data.map((item) => {
          // Initialize dailyData with calculated stock values
          const dailyData = dailyColumns.reduce((acc, col, index) => {
            const month = col.month;
            const dailySales = item.salesForecast[month] || 0;
            let stock;
            
            if (index === 0) {
              stock = item.inStockFBA - dailySales;
            } else {
              const prevDate = dailyColumns[index - 1].date;
              stock = acc[prevDate] - dailySales;
            }
            
            return { ...acc, [col.date]: Math.round(stock * 100) / 100 };
          }, {});

          // Calculate days until stock depletion and find due date
          let daysUntilDepletion = 0;
          let dueDate = '';
          for (let i = 0; i < dailyColumns.length; i++) {
            if (dailyData[dailyColumns[i].date] > 0) {
              daysUntilDepletion++;
            } else {
              dueDate = dailyColumns[i].date;
              break;
            }
          }

          // Calculate demand using the Google Sheets formula
          const currentMonth = currentDate.toLocaleString('en-US', { month: 'long' });
          const nextMonth = getNextMonth(currentMonth);
          const salesPerDayThisMonth = item.salesForecast[currentMonth] || 0;
          const salesPerDayNextMonth = item.salesForecast[nextMonth] || 0;
          const bagsPerBox = item.bagsPerBox || 1; // Prevent division by zero
          const demandCalc = (90 - salesPerDayNextMonth) * (salesPerDayThisMonth / bagsPerBox);
          const demand = demandCalc < 0 ? 0 : Math.round(demandCalc * 100) / 100;

          // Format dates to MM/DD/YYYY
          const formattedStartDate = new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          });
          const formattedDueDate = dueDate
            ? new Date(dueDate).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
              })
            : '';

          return {
            ...item,
            productName: item.productName,
            asin: item.ASIN,
            bagsPerBox: item.bagsPerBox,
            inStockFBA: item.inStockFBA,
            inStockWH: item.inStockWH,
            boxCanBeShipped: Math.floor(item.inStockWH / item.bagsPerBox) || 0,
            nextShipment: '',
            startDate: formattedStartDate, // Use formatted date
            dueDate: formattedDueDate, // Use formatted date
            demand,
            salesForecast: item.salesForecast,
            dailyData,
            stockColorClass: '', // Removed as it's no longer used
          };
        });
        setInventoryData(processedData);
      })
      .catch((error) => console.error('Error fetching inventory data:', error));
  }, []);

  return (
    <div className="inventory-fba-table-container">
      <table className="inventory-fba-table">
        <thead>
          <tr>
            <th rowSpan={2} className="inventory-fba-table-sticky-col">Product Name</th>
            <th rowSpan={2}>Bags Per Box</th>
            <th rowSpan={2}>ASIN</th>
            <th rowSpan={2}>Demand</th>
            <th rowSpan={2}>Box Can Be Shipped</th>
            <th rowSpan={2}>Next Shipment</th>
            <th rowSpan={2}>Stock FBA</th>
            <th rowSpan={2}>Stock WH</th>
            <th colSpan={12}>Sales Forecast Per Day</th>
            <th rowSpan={2}>Start Date</th>
            <th rowSpan={2}>Due Date</th>
            {dailyColumns.map((col) => (
              <th key={col.date}>{col.day}</th>
            ))}
          </tr>
          <tr>
            <th>July</th>
            <th>August</th>
            <th>September</th>
            <th>October</th>
            <th>November</th>
            <th>December</th>
            <th>January</th>
            <th>February</th>
            <th>March</th>
            <th>April</th>
            <th>May</th>
            <th>June</th>
            {dailyColumns.map((col) => (
              <th key={col.date}>{col.formattedDate}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {inventoryData.map((item, index) => (
            <tr key={index}>
              <td className="inventory-fba-table-sticky-col">{item.productName}</td>
              <td>{item.bagsPerBox}</td>
              <td>{item.asin}</td>
              <td>{item.demand}</td>
              <td>{item.boxCanBeShipped}</td>
              <td>{item.nextShipment}</td>
              <td>{item.inStockFBA}</td>
              <td>{item.inStockWH}</td>
              <td>{item.salesForecast.July}</td>
              <td>{item.salesForecast.August}</td>
              <td>{item.salesForecast.September}</td>
              <td>{item.salesForecast.October}</td>
              <td>{item.salesForecast.November}</td>
              <td>{item.salesForecast.December}</td>
              <td>{item.salesForecast.January}</td>
              <td>{item.salesForecast.February}</td>
              <td>{item.salesForecast.March}</td>
              <td>{item.salesForecast.April}</td>
              <td>{item.salesForecast.May}</td>
              <td>{item.salesForecast.June}</td>
              <td>{item.startDate}</td>
              <td>{item.dueDate}</td>
              {dailyColumns.map((col, i) => {
                const stock = item.dailyData[col.date] || 0;
                const month = col.month;
                const dailySales = item.salesForecast[month] || 0;

                let daysUntilDepletion = 0;
                if (dailySales === 0) {
                  if (stock > 0) {
                    daysUntilDepletion = dailyColumns.length - i;
                  } else {
                    daysUntilDepletion = 0;
                  }
                } else {
                  for (let j = i; j < dailyColumns.length; j++) {
                    const futureDate = dailyColumns[j].date;
                    const futureStock = item.dailyData[futureDate] || 0;
                    if (futureStock > 0) {
                      daysUntilDepletion++;
                    } else {
                      break;
                    }
                  }
                }

                let cellColor = '';
                if (stock <= 0) {
                  cellColor = 'red-cell';
                } else if (daysUntilDepletion < 15) {
                  cellColor = 'red-cell';
                } else if (daysUntilDepletion < 45) {
                  cellColor = 'orange-cell';
                } else if (daysUntilDepletion <= 90) {
                  cellColor = 'green-cell';
                } else {
                  cellColor = 'green-cell';
                }

                return (
                  <td key={col.date} className={cellColor}>
                    {stock}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TableInventoryFBA;